import React, { Component } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter, Redirect, Link } from 'react-router-dom';
import qs from 'qs';

import PurchaseList from 'components/PurchaseList/PurchaseList';
import Pagination from 'components/Pagination/Pagination';
import Spinner from 'components/Spinner';
import MergeOverduePurchases from 'components/MergeOverduePurchases/MergeOverduePurchases';
import {
  fetchUserPurchases,
  fetchUserCanceledPurchases,
  generateBlankInvoice,
} from 'actions/purchases';
import userPage from 'decorators/userPage';
import {
  getUserPurchases,
  getUserCancelledPurchases,
  getUserCancelledPurchasesStatus,
  getPageCount,
  getBlankInvoice,
  getLoggedInUser,
} from 'reducers';
import CheckboxInput from '../components/CheckboxInput';
import { getCarrierPrefix, prettifyCarrier } from 'helpers/subs';
import { can, permActions } from '../permissions';

class UserPurchases extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isBlankInvoiceCreated: false,
      showCancelled: false,
      error: '',
      cancelledError: '',
    };

    this.fetchPurchases = this.fetchPurchases.bind(this);
    this.generateBlankInvoice = this.generateBlankInvoice.bind(this);
  }

  componentDidMount() {
    this.fetchPurchases();
  }

  componentDidUpdate(oldProps) {
    if (oldProps.userId !== this.props.userId) {
      this.fetchPurchases();
    }

    if (oldProps.page !== this.props.page) {
      this.fetchPurchases();
    }

    if (oldProps.cancelledPage !== this.props.cancelledPage) {
      this.fetchCancelledPurchases();
    }

    if (oldProps.subscription !== this.props.subscription) {
      this.fetchPurchases();
      this.fetchCancelledPurchases();
    }
  }

  fetchPurchases() {
    const { fetchUserPurchases, userId, page, subscription, subCarrier } =
      this.props;
    const params = {
      [`${getCarrierPrefix(subCarrier)}subscription`]: subscription,
    };
    fetchUserPurchases(userId, page, params).catch((error) =>
      this.setState({ error: error.message }),
    );
  }

  fetchCancelledPurchases = () => {
    const {
      fetchUserCanceledPurchases,
      userId,
      cancelledPage,
      subscription,
      subCarrier,
    } = this.props;
    const params = {
      [`${getCarrierPrefix(subCarrier)}subscription`]: subscription,
    };
    fetchUserCanceledPurchases(userId, cancelledPage, params).catch((error) =>
      this.setState({ cancelledError: error.message }),
    );
  };

  handleShowCancelled = () => {
    if (!this.state.showCancelled) {
      this.fetchCancelledPurchases();
    }

    this.setState((state) => {
      return { showCancelled: !state.showCancelled };
    });
  };

  async generateBlankInvoice() {
    const { generateBlankInvoice, userId } = this.props;
    try {
      await generateBlankInvoice(userId);

      this.setState({ isBlankInvoiceCreated: true, error: '' });
    } catch (error) {
      this.setState({ error: error.message });
    }
  }

  render() {
    const {
      user,
      userId,
      purchases,
      cancelledPurchases,
      pageCount,
      cancelledPageCount,
      page,
      cancelledPage,
      blankInvoice,
      cancelledPurchasesStatus,
      subscription,
      subCarrier,
      loggedInUser,
    } = this.props;
    const { showCancelled, error, cancelledError, isBlankInvoiceCreated } =
      this.state;
    const showCancelledLoader =
      cancelledPurchasesStatus && cancelledPurchasesStatus.pending;
    if (!user || !loggedInUser) {
      return <div>Loading...</div>;
    }
    const showPurchases = can(loggedInUser, permActions.VIEW_PURCHASES);
    if (!showPurchases) {
      return <Redirect to={`/users/${userId}/view`} />;
    }
    if (isBlankInvoiceCreated && blankInvoice.length) {
      this.setState({ isBlankInvoiceCreated: false });

      return <Redirect to={`/purchases/${blankInvoice[0].id}/view`} />;
    }

    return (
      <div>
        <div className="flex-justify-space-between">
          <h1>Purchases</h1>
          <div>
            <MergeOverduePurchases
              userId={userId}
              onComplete={this.fetchPurchases}
            />
            <button
              className="btn btn-default ml-10"
              onClick={this.generateBlankInvoice}
            >
              Create blank invoice
            </button>
          </div>
        </div>
        {subscription && (
          <div>
            <div>
              {prettifyCarrier(subCarrier)} subscription {subscription}{' '}
              purchases
            </div>
            <Link to={`/users/${userId}/purchases`}>View user purchases</Link>
          </div>
        )}
        {error && (
          <div style={{ color: 'red', fontWeight: 'bold' }}>{error}</div>
        )}
        <PurchaseList purchases={purchases} />
        <Pagination
          count={pageCount}
          current={page}
          url={(pageNum) => `/users/${userId}/purchases?page=${pageNum}`}
        />
        <div className="mar-t">
          <CheckboxInput
            label="Show cancelled purchases"
            input={{
              value: showCancelled,
              onChange: this.handleShowCancelled,
            }}
          />
        </div>
        {showCancelled && (
          <React.Fragment>
            <h3>Cancelled purchases</h3>
            {cancelledError && (
              <div style={{ color: 'red', fontWeight: 'bold' }}>
                {cancelledError}
              </div>
            )}
            {showCancelledLoader ? (
              <div style={{ position: 'relative', height: '200px' }}>
                <Spinner showLoader={showCancelledLoader} />
              </div>
            ) : (
              <React.Fragment>
                <PurchaseList purchases={cancelledPurchases} />
                <Pagination
                  count={cancelledPageCount}
                  current={cancelledPage}
                  url={(pageNum) =>
                    `/users/${userId}/purchases?cancelledPage=${pageNum}`
                  }
                />
              </React.Fragment>
            )}
          </React.Fragment>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state, { location: { search } }) => {
  const {
    page = 1,
    cancelledPage = 1,
    subscription,
    subCarrier,
  } = qs.parse(search, { ignoreQueryPrefix: true });
  return {
    purchases: getUserPurchases(state, page),
    pageCount: getPageCount(state, 'purchases', `fetchByUser`, page),
    cancelledPurchases: getUserCancelledPurchases(state, cancelledPage),
    cancelledPurchasesStatus: getUserCancelledPurchasesStatus(
      state,
      cancelledPage,
    ),
    cancelledPageCount: getPageCount(
      state,
      'purchases',
      `fetchCancelledByUser`,
      cancelledPage,
    ),
    blankInvoice: getBlankInvoice(state),
    loggedInUser: getLoggedInUser(state),
    page,
    cancelledPage,
    subscription,
    subCarrier,
  };
};

const mapDispatchToProps = {
  fetchUserPurchases,
  fetchUserCanceledPurchases,
  generateBlankInvoice,
};

export default compose(
  withRouter,
  userPage,
  connect(mapStateToProps, mapDispatchToProps),
)(UserPurchases);
